import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface Props {
  children: ReactNode
  /** Optional custom fallback — receives the error and a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

/**
 * Top-level React error boundary.
 * Wrap around <RouterProvider> in main.tsx to catch any render error
 * that escapes the router's own errorElement handling.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  reset = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset)
    }

    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--bg, #f9f9f9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        fontFamily: 'system-ui, sans-serif',
      }}>
        <div style={{ width: '100%', maxWidth: 440 }}>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
              background: 'rgba(229,34,42,0.09)',
              border: '1px solid rgba(229,34,42,0.20)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AlertTriangle size={18} color="#e5222a" />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#111', letterSpacing: -0.4, lineHeight: 1.2 }}>
                Application error
              </div>
              <div style={{ fontSize: 13, color: '#666', marginTop: 5, lineHeight: 1.5 }}>
                {error.message || 'An unexpected error occurred.'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 7,
                background: '#111', color: '#fff',
                border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
                fontFamily: 'inherit',
              }}
            >
              <RefreshCw size={13} />
              Reload
            </button>
            <button
              onClick={this.reset}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 7,
                background: 'transparent', color: '#555',
                border: '1px solid #ddd', cursor: 'pointer',
                fontSize: 13, fontWeight: 600,
                fontFamily: 'inherit',
              }}
            >
              Try again
            </button>
          </div>

          {error.stack && (
            <pre style={{
              margin: 0,
              padding: '10px 14px',
              background: '#fff',
              border: '1px solid #eee',
              borderRadius: 8,
              fontSize: 10,
              lineHeight: 1.6,
              color: '#e5222a',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              maxHeight: 200,
              overflowY: 'auto',
            }}>
              {error.stack}
            </pre>
          )}

        </div>
      </div>
    )
  }
}
